import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'

import { round, displayFormula } from 'altrac-conversions'
import { DecimalInput, isNumber } from '../Inputs/Inputs'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'
import RedX from '../Graphics/RedX'

const DEFAULT_PHYSICAL = {
  min: 0,
  max: 100,
  zero: 0,
  precision: 1,
}

function pressure (formula, multiplier, valueKey, reading0, physical) {
  if (reading0[valueKey] > 0) {
    return displayFormula(
      formula,
      multiplier,
      0,
      null,
      valueKey,
      reading0,
      null,
      physical
    )
  }
  return 0
}

function ValveInputs (props) {
  const navigate = useNavigate()

  const {
    device: {
      physical = DEFAULT_PHYSICAL,
      application_settings: applicationSettings,
      application_settings_new: applicationSettingsNew,
    } = {},
    device,
    targetDevice,
    setValidity,
  } = props

  const {
    interface_versioned: {
      data: {
        other: {
          valveNumber: targetNumber = 1,
        } = {},
      } = {},
    },
  } = device

  const {
    settings: {
      calibrate1,
      calibrate2,
    } = {},
  } = applicationSettings

  const {
    settings: {
      calibrate1: newCalibrate1,
      calibrate2: newCalibrate2,
    } = {},
  } = applicationSettingsNew

  const {
    [`min${targetDevice}${targetNumber}`]: min = DEFAULT_PHYSICAL.min,
    [`max${targetDevice}${targetNumber}`]: max = DEFAULT_PHYSICAL.max,
    [`zero${targetDevice}${targetNumber}`]: zero = DEFAULT_PHYSICAL.zero,
    [`precision${targetDevice}${targetNumber}`]: precision = DEFAULT_PHYSICAL.precision,
  } = physical

  const [formIsValid, setFormIsValid] = useState(true)
  const [invalidFields, setInvalidFields] = useState({})
  const [minHolder, setMinHolder] = useState(min)
  const [maxHolder, setMaxHolder] = useState(max)
  const [zeroHolder, setZeroHolder] = useState(zero)
  const [precisionHolder, setPrecisionHolder] = useState(precision)
  const [calibrate1State, setCalibrate1State] = useState(newCalibrate1 || calibrate1)
  const [calibrate2State, setCalibrate2State] = useState(newCalibrate2 || calibrate2)

  const appDispatch = useCallback(() => {
    const physicals = {
      [`min${targetDevice}${targetNumber}`]: minHolder,
      [`max${targetDevice}${targetNumber}`]: maxHolder,
      [`zero${targetDevice}${targetNumber}`]: Number(zeroHolder),
      [`precision${targetDevice}${targetNumber}`]: Number(precisionHolder),
    }

    AppDispatcher.dispatch({
      action: 'change-physical',
      setting: physicals,
    })
  }, [targetDevice, targetNumber, minHolder, maxHolder, zeroHolder, precisionHolder])

  useEffect(() => {
    appDispatch()
  }, [targetDevice, targetNumber, minHolder, maxHolder, zeroHolder, precisionHolder])

  const childChange = useCallback((event) => {
    const { target: { name, value, validity: { valid } } } = event
    let newValue = value

    switch (name) {
      case 'minHolder':
        newValue = round(value, 3)
        setMinHolder(newValue)
        break
      case 'maxHolder':
        newValue = round(value, 3)
        setMaxHolder(newValue)
        break
      case 'zeroHolder':
        newValue = round(value, 3)
        setZeroHolder(newValue)
        break
      case 'precisionHolder':
        newValue = round(value, 0)
        setPrecisionHolder(newValue)
        break
      default:
        return
    }

    const newInvalidFields = { ...invalidFields }

    if (valid) {
      delete newInvalidFields[name]
    } else {
      newInvalidFields[name] = true
    }

    const newFormIsValid = Object.keys(newInvalidFields).length === 0

    setInvalidFields(newInvalidFields)
    setFormIsValid(newFormIsValid)
    setValidity(newFormIsValid)
  }, [invalidFields, setValidity, appDispatch])

  const changeFeedbackThreshold = useCallback((input) => {
    const { id: deviceID, customer_id: customerID } = device
    navigate(`/${customerID}/devices/${deviceID}/settings/feedbackThreshold?input=${input}`)
  }, [device])

  const {
    interface_versioned: {
      data: {
        other: {
          valveNumber: valveNumberInterface = 1,
        } = {},
      } = {},
    },
    physical: devicePhysical,
  } = device

  const unitStyle = { opacity: 0.7, marginBottom: '10px' }
  const redX = (
    <RedX
      style={{
        position: 'absolute',
        height: '20px',
        right: '4px',
        top: '8px',
        width: '20px',
      }}
    />
  )

  const textInputStyle = {
    height: '32px',
    position: 'relative',
    width: '100%',
  }

  const valueInRange = (
    value,
    min = 0,
    max = Number.MAX_VALUE
  ) => isNumber(value) && ((value < min) || (value > max)) && redX

  return (
    <div>
      <section className={styles.heading}>
        <h3>
          {valveNumberInterface === 1
            ? (
              <FormattedMessage
                id='setting.device.SDinput1.label'
                defaultMessage='SD Input 1'
                description='SD Input 1 label'
              />
              )
            : (
              <FormattedMessage
                id='setting.device.SDinput2.label'
                defaultMessage='SD Input 2'
                description='SD Input 2 label'
              />
              )}
        </h3>
      </section>
      <div className={styles.inputArea}>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.fourToTwentyScale.min'
              defaultMessage='Value Min'
              description='Value Min label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(minHolder * 10) / 10}
                childChange={childChange}
                field='minHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.fourToTwentyScale.max'
              defaultMessage='Value Max'
              description='Value Max label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(maxHolder * 10) / 10}
                childChange={childChange}
                field='maxHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.fourToTwentyScale.zero'
              defaultMessage='Zero'
              description='Zero label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={Math.round(zeroHolder * 10) / 10}
                childChange={childChange}
                field='zeroHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.device.fourToTwentyScale.precision'
              defaultMessage='Precision'
              description='Precision label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <div style={textInputStyle}>
              <DecimalInput
                value={round(precisionHolder, 0)}
                childChange={childChange}
                field='precisionHolder'
                min={0}
                style={styles.controlNumberInput}
              />
            </div>
          </div>
        </section>
        <section>
          <div>
            <h5>
              <FormattedMessage
                id='setting.pump.analogSensor.feedbackThreshold'
                defaultMessage='Feedback Threshold'
                description='Feedback Threshold label'
              />
            </h5>
            <button
              className={styles.thresholdButton}
              name='thresholdButton'
              onClick={() => changeFeedbackThreshold(valveNumberInterface)}
              type='button'
            >
              {valveNumberInterface === 1
                ? pressure('fourToTwentySD1', 10000, '142', { 142: calibrate1State }, devicePhysical) || '7 (Default)'
                : pressure('fourToTwentySD2', 10000, '143', { 143: calibrate2State }, devicePhysical) || '7 (Default)'}
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}

ValveInputs.propTypes = {
  device: PropTypes.object.isRequired,
  setValidity: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
}

export default injectIntl(ValveInputs)
